import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CartItem {
  productId: string;
  name: string;
  image: string;
  price: number;
  orderType: 'particulier' | 'magasin';
  weight: number;
  quantity?: number;
  boxCount?: number;
  unitPrice: number;
  totalPrice: number;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: {
    productId: string;
    name: string;
    image: string;
    price: number;
  }, quantity: number, orderType: 'particulier' | 'magasin', weight: number, boxCount?: number) => void;
  removeFromCart: (productId: string, orderType: 'particulier' | 'magasin', weight: number) => void;
  clearCart: () => void;
  totalItems: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('noza_cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('noza_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (
    product: { productId: string; name: string; image: string; price: number },
    quantity: number,
    orderType: 'particulier' | 'magasin',
    weight: number,
    boxCount?: number
  ) => {
    const unitPrice = product.price;
    let totalPrice = 0;
    
    if (orderType === 'particulier') {
      totalPrice = unitPrice * quantity;
    } else {
      totalPrice = unitPrice * 10 * (boxCount || 1);
    }

    const newItem: CartItem = {
      productId: product.productId,
      name: product.name,
      image: product.image,
      price: product.price,
      orderType,
      weight,
      quantity: orderType === 'particulier' ? quantity : undefined,
      boxCount: orderType === 'magasin' ? boxCount : undefined,
      unitPrice,
      totalPrice,
    };

    setCartItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.productId === product.productId && item.orderType === orderType && item.weight === weight
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        if (orderType === 'particulier') {
          updated[existingIndex].quantity = (updated[existingIndex].quantity || 0) + quantity;
        } else {
          updated[existingIndex].boxCount = (updated[existingIndex].boxCount || 0) + (boxCount || 1);
        }
        updated[existingIndex].totalPrice = orderType === 'particulier'
          ? unitPrice * (updated[existingIndex].quantity || 0)
          : unitPrice * 10 * (updated[existingIndex].boxCount || 0);
        return updated;
      }

      return [...prev, newItem];
    });
  };

  const removeFromCart = (productId: string, orderType: 'particulier' | 'magasin', weight: number) => {
    setCartItems((prev) =>
      prev.filter((item) => !(item.productId === productId && item.orderType === orderType && item.weight === weight))
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('noza_cart');
  };

  const totalItems = cartItems.reduce((sum, item) => {
    if (item.orderType === 'particulier') {
      return sum + (item.quantity || 0);
    }
    return sum + (item.boxCount || 0);
  }, 0);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, clearCart, totalItems }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}